import { Inject, Injectable } from '@angular/core';
import { APPLICATION_NAME_TOKEN, Audience, Partner, ViewCategory, VisibilityType } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ApplicationNames, Country, LocaleToCountry } from '@ddt-web/domain';
import { LocalStorageKey } from '@ddt-web/generic-utils';
import { UrlUtils } from '@ddt-web/url';
import { PartnerConstants, UserDataService } from '@ddt-web/user-data';
import * as get from 'lodash/get';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { first, map } from 'rxjs/operators';

/**
 * Identifies the user/audience in terms of view category, country, and partner
 * Populates the audience object used for visibility config resolution
 */
@Injectable({
	providedIn: 'root'
})
export class AudienceService {
	@InjectConfig(SharedConfigKey.ENABLE_EXTSSO_CHECK) private readonly isExtSsoCheckEnabled: boolean = false;
	private audience$: Subject<Audience> = new ReplaySubject(null);
	private viewCategory$: Subject<ViewCategory> = new ReplaySubject(null);
	private visibility$: Subject<VisibilityType> = new ReplaySubject(null);

	constructor(
		private userDataService: UserDataService,
		@Inject(APPLICATION_NAME_TOKEN) private appName
	) { }

	public initialiseAudience(viewCategory: ViewCategory): void {
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.getUserProfileInformation()
			.subscribe(({ ssoInitiatedFrom, isInternalUser }) => {
				this.audience$.next({ country: this.resolveCountry(isInternalUser), partner: ssoInitiatedFrom });
				this.setViewCategory(viewCategory);
			});
		this.setVisibilityType(VisibilityType.LOGGED_IN_VIEW);
	}

	// For apps without data layer (such as AC Dashboard and PortfolioLibrary)
	public initialiseAudienceWithoutViewCategory(): void {
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.getUserProfileInformation()
			.subscribe(({ ssoInitiatedFrom, isInternalUser }) => {
				this.audience$.next({ country: this.resolveCountry(isInternalUser), partner: ssoInitiatedFrom });
			});
	}

	public initialiseAudienceWithVisibility(visibility: VisibilityType, viewCategory: ViewCategory) {
		if (visibility === VisibilityType.PUBLIC_VIEW) {
			this.audience$.next({ country: this.resolveCountry(false) });
		}
		else {
			// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
			this.getUserProfileInformation()
				.subscribe(({ ssoInitiatedFrom, isInternalUser }) => {
					this.audience$.next({ country: this.resolveCountry(isInternalUser), partner: ssoInitiatedFrom });
				});
		}

		this.setVisibilityType(visibility);
		this.setViewCategory(viewCategory);
	}

	private getUserProfileInformation(): Observable<{ ssoInitiatedFrom: Partner; isInternalUser: boolean }> {
		return this.userDataService.getUserProfile()
			.pipe(
				map(userData => {
					let ssoInitiatedFrom = get(userData, PartnerConstants.SSO_TOKEN_PATH, localStorage.getItem(LocalStorageKey.SsoInitiatedFrom));
					if (this.isExtSsoCheckEnabled) {
						const isExternalSso = get(userData, PartnerConstants.EXT_SSO_TOKEN_PATH, localStorage.getItem(LocalStorageKey.ExtSso)) === 'true';
						ssoInitiatedFrom = isExternalSso ? ssoInitiatedFrom : null;
					}
					return {
						ssoInitiatedFrom,
						isInternalUser: userData?.internalUser ?? false
					};
				}),
				first()
			);
	}

	public getViewCategory(): Observable<ViewCategory> {
		return this.viewCategory$;
	}

	public setViewCategory(viewCategory: ViewCategory): void {
		this.viewCategory$.next(viewCategory);
	}

	public getAudience(): Observable<Audience> {
		return this.audience$;
	}
	public setAudience(audience: Audience): void {
		this.audience$.next(audience);
	}

	public getVisibilityType(): Observable<VisibilityType> {
		return this.visibility$;
	}

	public setVisibilityType(visibility: VisibilityType): void {
		this.visibility$.next(visibility);
	}

	private resolveCountry(isInternalUser: boolean): Country {
		const mockCountry: string = localStorage.getItem(LocalStorageKey.MockCountry);
		if (!UrlUtils.isProdDmz() && isInternalUser && Boolean(mockCountry)) {
			return Country[mockCountry] || Country.default;

		} else {
			// BPAS app is country agnostic so checking that we don't match it for GB
			if (this.appName === ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION) {
				return Country.default;
			} else {
				return this.resolveCountryFromMetadata() || this.resolveCountryFromUrl() || Country.default;
			}
		}
	}

	private resolveCountryFromMetadata(): Country {
		const locale = document.querySelector('meta[property="og:locale"]');
		const content = locale ? locale.getAttribute('content') : null;
		return Country[content];
	}

	private resolveCountryFromUrl(): Country {
		const pathname = window.location.href;
		// BPAS is country agnostic so checking that we don't match it for GB
		if (pathname.includes('en-gb') && this.appName !== ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION) {
			return Country.en_GB;
		} else if (pathname.includes('/us/')) {
			return Country.en_US;
		}

		for (const [countryCode, countryValue] of Object.entries(LocaleToCountry)) {
			if (pathname.includes(countryCode)) {
				return Country[countryValue];
			}
		}

		return null;
	}
}
